"use strict";
/**
 * Polymarket API Client (Proxy Mode)
 * Calls local proxy server at same base URL as Kalshi; uses same session token.
 * Browser → Proxy Server → Polymarket Gamma/CLOB APIs
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PolymarketApiError = void 0;
exports.getSports = getSports;
exports.getEvents = getEvents;
exports.getMarketBySlug = getMarketBySlug;
exports.getPositions = getPositions;
exports.placeOrder = placeOrder;
exports.polymarketEventToDisplayEvent = polymarketEventToDisplayEvent;
exports.polymarketEventMarketToMarket = polymarketEventMarketToMarket;
const api_1 = require("./api");
const auth_1 = require("./auth");
const state_1 = require("./state");
// =============================================================================
// Error handling
// =============================================================================
class PolymarketApiError extends Error {
    constructor(message, statusCode, responseData) {
        super(message);
        this.statusCode = statusCode;
        this.responseData = responseData;
        this.name = "PolymarketApiError";
    }
}
exports.PolymarketApiError = PolymarketApiError;
// =============================================================================
// Request helper (same proxy + session token as Kalshi)
// =============================================================================
async function polymarketRequest(method, path, params, body) {
    const sessionToken = (0, auth_1.getSessionToken)();
    if (!sessionToken) {
        throw new Error("Not authenticated. Please login.");
    }
    let url = `${(0, api_1.getApiBaseUrl)()}${path}`;
    if (params && Object.keys(params).length > 0) {
        const search = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => search.set(k, String(v)));
        const qs = search.toString();
        if (qs)
            url += `?${qs}`;
    }
    const options = {
        method,
        headers: {
            "Content-Type": "application/json",
            "X-Session-Token": sessionToken,
        },
    };
    if (body) {
        options.body = JSON.stringify(body);
    }
    const response = await fetch(url, options);
    const data = await response.json();
    if (!response.ok) {
        // Check if session expired
        if (response.status === 401 && data.code === "INVALID_SESSION") {
            // Clear local session and redirect to login
            (0, auth_1.clearSessionToken)();
            (0, state_1.updateState)({
                isAuthenticated: false,
                sessionToken: null,
                currentPage: "login",
                errorMessage: "Session expired. Please login again.",
            });
        }
        throw new PolymarketApiError(data?.error ||
            data?.message ||
            `Polymarket API error: ${response.status}`, response.status, data);
    }
    return data;
}
/**
 * Fetch list of available sports from Polymarket.
 */
async function getSports() {
    const res = await polymarketRequest("GET", "/polymarket/sports");
    if (!res.success || !Array.isArray(res.data)) {
        throw new PolymarketApiError("Invalid sports response", 0, res);
    }
    return res.data;
}
/**
 * Fetch events from Polymarket with optional filters.
 * Use Sport.series as sportSeriesId when filtering by sport.
 */
async function getEvents(params = {}) {
    const query = {};
    if (params.sportSeriesId != null)
        query.sportSeriesId = params.sportSeriesId;
    if (params.active != null)
        query.active = params.active;
    if (params.limit != null)
        query.limit = params.limit;
    if (params.sortByDate != null)
        query.sortByDate = params.sortByDate;
    if (params.tagSlug != null)
        query.tagSlug = params.tagSlug;
    const res = await polymarketRequest("GET", "/polymarket/events", query);
    if (!res.success || !Array.isArray(res.data)) {
        throw new PolymarketApiError("Invalid events response", 0, res);
    }
    return res.data;
}
/**
 * Fetch a single market by slug.
 */
async function getMarketBySlug(slug) {
    const res = await polymarketRequest("GET", `/polymarket/markets/${encodeURIComponent(slug)}`);
    if (!res.success || !res.data) {
        throw new PolymarketApiError("Invalid market response", 0, res);
    }
    return res.data;
}
/**
 * Fetch user's positions from Polymarket.
 * Requires authenticated wallet address in session.
 *
 * @param conditionId - Optional condition ID to filter positions for a specific market
 */
async function getPositions(conditionId) {
    const query = {};
    if (conditionId) {
        query.conditionId = conditionId;
    }
    const res = await polymarketRequest("GET", "/polymarket/positions", query);
    if (!res.success || !Array.isArray(res.data)) {
        throw new PolymarketApiError("Invalid positions response", 0, res);
    }
    return res.data;
}
/**
 * Place an order on Polymarket.
 * Supports market orders (buy/sell), position flipping, and batch orders.
 *
 * @param orderRequest - Order parameters
 */
async function placeOrder(orderRequest) {
    const res = await polymarketRequest("POST", "/polymarket/orders", undefined, orderRequest);
    if (!res.success) {
        throw new PolymarketApiError("Order placement failed", 0, res);
    }
    return res;
}
// =============================================================================
// Adapters: Polymarket → unified display types
// =============================================================================
/**
 * Map a Polymarket event to the unified DisplayEvent shape for the list.
 */
function polymarketEventToDisplayEvent(e) {
    return {
        id: e.id,
        title: e.title,
        subtitle: e.description ?? "",
        category: e.seriesSlug ?? e.tags?.[0]?.label,
        scheduledDate: e.endDate,
    };
}
/**
 * Parse outcome prices (may be array or JSON string).
 */
function parseOutcomePrices(outcomePrices) {
    if (Array.isArray(outcomePrices)) {
        const yes = parseFloat(outcomePrices[0] ?? "0.5");
        const no = parseFloat(outcomePrices[1] ?? "0.5");
        return [yes, no];
    }
    if (typeof outcomePrices === "string") {
        try {
            const arr = JSON.parse(outcomePrices);
            return [parseFloat(arr[0] ?? "0.5"), parseFloat(arr[1] ?? "0.5")];
        }
        catch {
            return [0.5, 0.5];
        }
    }
    return [0.5, 0.5];
}
/**
 * Map a Polymarket event market to the app's Market shape (state + order flow).
 */
function polymarketEventMarketToMarket(m, eventContext) {
    const [yesPrice, noPrice] = parseOutcomePrices(m.outcomePrices);
    const bid = m.bestBid ?? yesPrice;
    const ask = m.bestAsk ?? 1 - noPrice;
    const status = m.closed ? "finalized" : m.active ? "active" : "inactive";
    // Parse clobTokenIds if it's a JSON string (Polymarket API sometimes returns it as string)
    let clobTokenIds = m.clobTokenIds;
    if (typeof clobTokenIds === "string") {
        try {
            clobTokenIds = JSON.parse(clobTokenIds);
        }
        catch (error) {
            console.warn("Failed to parse clobTokenIds:", clobTokenIds);
            clobTokenIds = undefined;
        }
    }
    // Parse outcomes if it's a JSON string (Polymarket API sometimes returns it as string)
    let outcomes = m.outcomes;
    if (typeof outcomes === "string") {
        try {
            outcomes = JSON.parse(outcomes);
        }
        catch (error) {
            console.warn("Failed to parse outcomes:", outcomes);
            outcomes = undefined;
        }
    }
    let yesSubtitle = "Yes";
    let noSubtitle = "No";
    if (outcomes && Array.isArray(outcomes) && outcomes.length >= 2) {
        yesSubtitle = outcomes[0] || "Yes";
        noSubtitle = outcomes[1] || "No";
    }
    const market = {
        id: m.slug,
        venue: "polymarket",
        title: m.question,
        subtitle: eventContext?.title ?? "",
        category: "polymarket",
        strike_price: 0.5,
        status,
        bid: Number.isFinite(bid) ? bid : yesPrice,
        ask: Number.isFinite(ask) ? ask : yesPrice,
        yesSubtitle,
        noSubtitle,
        clobTokenIds, // Store asset IDs for orderbook subscription
        outcomes, // Store outcomes array for reference
        conditionId: m.conditionId, // Store condition ID for position filtering
    };
    return market;
}
